import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import type { createWorkflowsPaths } from "@circle-react/helpers/urlHelpers/internalApi/workflows";
import { workflows } from "@circle-react/helpers/urlHelpers/internalApi/workflows";
import type {
  Action,
  ListWorkflows,
  Trigger,
} from "../components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import type { ActionWithDefinedType } from "../components/SettingsApp/Workflows/types";
import type {
  BodyParamsProps,
  BodyProps,
  IdBodyProps,
  IdProps,
  ParamsProps,
  TaskIdProps,
  TemplateIdProps,
  TemplateIdWithOverridesProps,
  TemplateKeyProps,
  WorkflowIdBodyProps,
  WorkflowIdIdBodyProps,
  WorkflowIdIdProps,
  WorkflowIdProps,
  WorkflowIdRunIdProps,
} from "./workflowsApi.types";

export const createWorkflowsApi = (
  workflowsPaths: ReturnType<typeof createWorkflowsPaths>,
) => {
  const list = ({ params = {} }: ParamsProps) =>
    reactQueryGet<ListWorkflows>(workflowsPaths.list({ params }));

  const listHistory = ({ params = {} }: ParamsProps) =>
    reactQueryGet(workflowsPaths.tasks({ params }));

  const taskDetail = ({ taskId, params = {} }: TaskIdProps) =>
    reactQueryGet(workflowsPaths.taskDetail({ params, taskId }));

  const create = ({ params = {}, body = {} }: BodyParamsProps) =>
    reactQueryPost(workflowsPaths.create({ params }), body);

  const fetch = ({ id, params = {} }: IdProps) =>
    reactQueryGet(workflowsPaths.fetch({ id, params }));

  const update = ({ id, params = {}, body = {} }: IdBodyProps) =>
    reactQueryPut(workflowsPaths.update({ id, params }), body);

  const activate = ({ id, params = {} }: IdProps) =>
    reactQueryPut<undefined, object>(workflowsPaths.activate({ id, params }));

  const inactivate = ({ id, params = {} }: IdProps) =>
    reactQueryPut<undefined, object>(workflowsPaths.inactivate({ id, params }));

  const createTrigger = ({
    workflowId,
    params = {},
    body = {},
  }: WorkflowIdBodyProps) =>
    reactQueryPost<Trigger>(
      workflowsPaths.createTrigger({ workflowId, params }),
      body,
    );

  const updateTrigger = ({
    workflowId,
    id,
    params = {},
    body = {},
  }: WorkflowIdIdBodyProps) =>
    reactQueryPut<Trigger, any>(
      workflowsPaths.updateTrigger({ workflowId, id, params }),
      body,
    );

  const deleteTrigger = ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    reactQueryDelete(workflowsPaths.deleteTrigger({ workflowId, id, params }));

  const createRun = ({ workflowId, params = {} }: WorkflowIdProps) =>
    reactQueryPost<undefined>(workflowsPaths.createRun({ workflowId, params }));

  const fetchRuns = ({ workflowId, params = {} }: WorkflowIdProps) =>
    reactQueryGet(workflowsPaths.fetchRuns({ workflowId, params }));

  const fetchRun = ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
    reactQueryGet(workflowsPaths.fetchRun({ workflowId, runId, params }));

  const cancelRun = ({
    workflowId,
    runId,
    params = {},
  }: WorkflowIdRunIdProps) =>
    reactQueryPut(workflowsPaths.cancelRun({ workflowId, runId, params }));

  const fetchTasksInRun = ({
    workflowId,
    runId,
    params = {},
  }: WorkflowIdRunIdProps) =>
    reactQueryGet(
      workflowsPaths.fetchTasksInRun({ workflowId, runId, params }),
    );

  const createAudience = ({
    workflowId,
    params = {},
    body = {},
  }: WorkflowIdBodyProps) =>
    reactQueryPost(workflowsPaths.createAudience({ workflowId, params }), body);

  const updateAudience = ({
    workflowId,
    id,
    params = {},
    body = {},
  }: WorkflowIdIdBodyProps) =>
    reactQueryPut(
      workflowsPaths.updateAudience({ workflowId, id, params }),
      body,
    );

  const createAction = ({
    workflowId,
    params = {},
    body = {},
  }: WorkflowIdBodyProps) =>
    reactQueryPost<Action>(
      workflowsPaths.createAction({ workflowId, params }),
      body,
    );

  const updateAction = ({
    workflowId,
    id,
    params = {},
    body = {},
  }: WorkflowIdIdBodyProps) =>
    reactQueryPut<Action, any>(
      workflowsPaths.updateAction({ workflowId, id, params }),
      body,
    );

  const deleteAction = ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    reactQueryDelete<undefined, object>(
      workflowsPaths.deleteAction({ workflowId, id, params }),
    );

  const upsertActions = ({
    workflowId,
    params = {},
    body = {},
  }: WorkflowIdBodyProps) =>
    reactQueryPost<ActionWithDefinedType[]>(
      workflowsPaths.upsertActions({ workflowId, params }),
      body,
    );

  const createOneTimeSchedule = ({
    workflowId,
    params = {},
    body = {},
  }: WorkflowIdBodyProps) =>
    reactQueryPost(
      workflowsPaths.createOneTimeSchedule({ workflowId, params }),
      body,
    );

  const createRecurringSchedule = ({
    workflowId,
    params = {},
    body = {},
  }: WorkflowIdBodyProps) =>
    reactQueryPost(
      workflowsPaths.createRecurringSchedule({ workflowId, params }),
      body,
    );

  const updateOneTimeSchedule = ({
    workflowId,
    id,
    params = {},
    body = {},
  }: WorkflowIdIdBodyProps) =>
    reactQueryPut(
      workflowsPaths.updateOneTimeSchedule({ workflowId, id, params }),
      body,
    );

  const updateRecurringSchedule = ({
    workflowId,
    id,
    params = {},
    body = {},
  }: WorkflowIdIdBodyProps) =>
    reactQueryPut(
      workflowsPaths.updateRecurringSchedule({ workflowId, id, params }),
      body,
    );

  const reschedule = ({
    workflowId,
    id,
    params = {},
    body = {},
  }: WorkflowIdIdBodyProps) =>
    reactQueryPut(workflowsPaths.reschedule({ workflowId, id, params }), body);

  const templates = ({ params = {} }: ParamsProps) =>
    reactQueryGet(workflowsPaths.templates({ params }));

  const fetchTemplate = ({ templateId, params = {} }: TemplateIdProps) =>
    reactQueryGet(workflowsPaths.fetchTemplate({ templateId, params }));

  const createFromTemplate = ({
    templateId,
    params = {},
    overrides = {},
  }: TemplateIdWithOverridesProps) =>
    reactQueryPost(
      workflowsPaths.createFromTemplate({ templateId, params }),
      overrides,
    );

  const createFromTemplateKey = ({ templateKey }: TemplateKeyProps) =>
    reactQueryPost<{ id: string }>(
      workflowsPaths.createFromTemplateKey({ templateKey }),
    );

  const archiveWorkflow = ({ id, params = {} }: IdProps) =>
    reactQueryPut(workflowsPaths.archive({ id, params }));

  const duplicateWorkflow = ({ id, params = {} }: IdProps) =>
    reactQueryPut(workflowsPaths.duplicate({ id, params }));

  const deleteWorkflow = ({ id, params = {} }: IdProps) =>
    reactQueryDelete(workflowsPaths.delete({ id, params }));

  const fetchCommunityCurrentUsageStats = ({ params = {} } = {}) =>
    reactQueryGet(workflowsPaths.fetchCommunityCurrentUsageStats({ params }));

  const fetchPreviewAttributes = ({ params = {} }: ParamsProps) =>
    reactQueryGet(workflowsPaths.previewMessages.previewAttributes({ params }));

  const fetchInterpolatedPreviewMessage = ({ body = {} }: BodyProps) =>
    reactQueryPost(workflowsPaths.fetchInterpolatedMessage({}), body);

  const testWebhook = ({ body = {} }: BodyProps) =>
    reactQueryPost(workflowsPaths.testWebhook({}), body);

  const fetchContacts = ({ params = {} }: ParamsProps) =>
    reactQueryGet(workflowsPaths.contacts.index({ params }));

  return {
    list,
    listHistory,
    taskDetail,
    create,
    update,
    fetch,
    activate,
    inactivate,
    createTrigger,
    updateTrigger,
    deleteTrigger,
    createRun,
    fetchRuns,
    fetchRun,
    fetchTasksInRun,
    createAudience,
    updateAudience,
    createAction,
    updateAction,
    deleteAction,
    upsertActions,
    createOneTimeSchedule,
    updateOneTimeSchedule,
    createRecurringSchedule,
    updateRecurringSchedule,
    reschedule,
    templates,
    fetchTemplate,
    createFromTemplate,
    createFromTemplateKey,
    archiveWorkflow,
    deleteWorkflow,
    fetchCommunityCurrentUsageStats,
    duplicateWorkflow,
    fetchPreviewAttributes,
    fetchInterpolatedPreviewMessage,
    testWebhook,
    cancelRun,
    fetchContacts,
  };
};
/** @deprecated Use `useWorkflowsApi()` hook instead for feature flag support. */
export const workflowsApi = createWorkflowsApi(workflows);
